// FieldIQ — First-run onboarding tour

import { useState } from "react";
import {
  MessageSquare,
  Users,
  Loader2,
  ChevronRight,
  Search,
  DoorOpen,
  Activity,
  Megaphone,
  Bell,
} from "lucide-react";


const TOUR_KEYFRAMES = `
  @keyframes fiq-slide-in {
    from { opacity: 0; transform: translateX(18px); }
    to   { opacity: 1; transform: translateX(0); }
  }
  @keyframes fiq-notif {
    0%,8%   { opacity:0; transform:translateY(-22px); }
    20%,72% { opacity:1; transform:translateY(0); }
    87%,100% { opacity:0; transform:translateY(-14px); }
  }
  @keyframes fiq-bell {
    0%,18%,100% { transform:rotate(0deg) scale(1); }
    23% { transform:rotate(-22deg) scale(1.15); }
    31% { transform:rotate(18deg) scale(1.1); }
    39% { transform:rotate(-12deg); }
    47% { transform:rotate(9deg); }
    55% { transform:rotate(0deg); }
  }
`;

const SlideIllustration = ({ slideIndex }) => {
  // Slides 0-5 use real screen recordings; slide 6 (push) keeps a simple animation
  const videos = [
    "FieldIQ-Log-Interaction.mp4",
    "FieldIQ-Lidy.mp4",
    "FieldIQ-People.mp4",
    "FieldIQ-Activity.mp4",
    "FieldIQ-Lookup.mp4",
    "FieldIQ-Announcements.mp4",
  ];
  const src = videos[slideIndex];

  if (src) {
    return (
      <div className="mt-4 w-56 h-60 rounded-2xl overflow-hidden border border-slate-700/60 bg-black shadow-xl flex-shrink-0">
        <video
          key={src}
          src={`/intro-tour/${src}`}
          autoPlay
          loop
          muted
          playsInline
          className="w-full h-full object-cover object-top"
        />
      </div>
    );
  }

  // Push slide: no recording — keep the notification animation
  return (
    <div className="w-56 mt-5">
      <div
        style={{ animation: "fiq-notif 3.6s ease-in-out infinite" }}
        className="bg-slate-800 border border-slate-700 rounded-xl p-3 flex items-start gap-2.5"
      >
        <div
          style={{ animation: "fiq-bell 3.6s ease-in-out 0.4s infinite" }}
          className="text-cyan-400 mt-0.5 flex-shrink-0"
        >
          <Bell size={16} />
        </div>
        <div className="text-left">
          <div className="text-xs font-semibold text-white">
            New walk assigned
          </div>
          <div className="text-xs text-slate-400 mt-0.5">
            Precinct 22 — Oak St. corridor
          </div>
        </div>
      </div>
    </div>
  );
};

export const OnboardingTour = ({ onDone, onEnablePush, showPushSlide }) => {
  const [slide, setSlide] = useState(0);
  const [enabling, setEnabling] = useState(false);

  // Slides ordered to match tab nav: Today → Lidy → People → Activity → Lookup → Announcements → Push
  const slides = [
    {
      icon: DoorOpen,
      title: "Log interactions in seconds",
      body: "Tap a tile on the Today screen, pick the result, done. Add a voice note and FieldIQ transcribes and summarizes it for you.",
    },
    {
      icon: MessageSquare,
      title: "Ask Lidy anything",
      body: "Lidy is your AI field assistant. Ask about voters at an address, get talking points, or look up who skipped the last election.",
    },
    {
      icon: Users,
      title: "Activate your circle",
      body: "Import contacts from your phone to find the people you already know. A text from a friend turns out 8× more voters than one from a stranger.",
    },
    {
      icon: Activity,
      title: "See your team in action",
      body: "The Activity feed shows every interaction your team logs — who they talked to, what was said, and which topics came up most.",
    },
    {
      icon: Search,
      title: "Look up any address",
      body: "Type an address or tap the map pin to instantly pull up voter registration, party affiliation, and district info — right at the door.",
    },
    {
      icon: Megaphone,
      title: "Announcements from leadership",
      body: "Get walk assignments, event reminders, and district updates the moment they go out — no more group texts.",
    },
    ...(showPushSlide
      ? [
          {
            icon: Bell,
            title: "Stay in the loop",
            body: "Get announcements from leadership, walk assignments, and event reminders the moment they go out.",
            isPush: true,
          },
        ]
      : []),
  ];

  const current = slides[slide];
  const Icon = current.icon;
  const isLast = slide === slides.length - 1;

  // Nothing to remember: the tour is opened deliberately from Profile, so
  // there is no first-run state to mark as seen.
  const finish = () => onDone();

  const handleEnablePush = async () => {
    setEnabling(true);
    try {
      await onEnablePush();
    } catch {}
    setEnabling(false);
    finish();
  };

  return (
    <div className="fixed inset-0 z-[90] bg-slate-950 flex flex-col">
      <style>{TOUR_KEYFRAMES}</style>
      <div className="max-w-lg mx-auto w-full flex-1 flex flex-col px-6 pb-8 pt-4">
        {/* Skip */}
        <div className="flex justify-end">
          <button
            type="button"
            onClick={finish}
            className="text-sm text-slate-500 hover:text-slate-300 px-2 py-1"
          >
            Skip
          </button>
        </div>

        {/* Slide content */}
        <div
          key={slide}
          style={{ animation: "fiq-slide-in 0.28s ease-out" }}
          className="flex-1 flex flex-col items-center justify-center text-center"
        >
          <div className="h-16 w-16 rounded-2xl bg-cyan-600/15 border border-cyan-600/30 flex items-center justify-center mb-6">
            <Icon size={30} className="text-cyan-400" />
          </div>
          <h2 className="text-xl font-semibold text-white mb-3">
            {current.title}
          </h2>
          <p className="text-sm text-slate-400 leading-relaxed max-w-xs">
            {current.body}
          </p>
          <SlideIllustration slideIndex={slide} />
        </div>

        {/* Dots */}
        <div className="flex justify-center gap-2 mb-6">
          {slides.map((_, i) => (
            <span
              key={i}
              className={`h-1.5 rounded-full transition-all ${
                i === slide ? "w-5 bg-cyan-400" : "w-1.5 bg-slate-700"
              }`}
            />
          ))}
        </div>

        {/* Actions */}
        {current.isPush ? (
          <div className="space-y-2">
            <button
              type="button"
              onClick={handleEnablePush}
              disabled={enabling}
              className="w-full py-3 bg-cyan-600 hover:bg-cyan-500 disabled:opacity-60 text-white text-sm font-semibold rounded-xl flex items-center justify-center gap-2"
            >
              {enabling ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <Bell size={16} />
              )}
              Turn On Notifications
            </button>
            <button
              type="button"
              onClick={finish}
              className="w-full py-3 text-slate-400 hover:text-slate-200 text-sm font-medium"
            >
              Not now
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => (isLast ? finish() : setSlide(slide + 1))}
            className="w-full py-3 bg-cyan-600 hover:bg-cyan-500 text-white text-sm font-semibold rounded-xl flex items-center justify-center gap-2"
          >
            {isLast ? "Get Started" : "Next"}
            <ChevronRight size={16} />
          </button>
        )}
      </div>
    </div>
  );
};
